import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';
import { COCKPIT_CSS } from '../cockpit/cockpitStyles';
import { CRM_CSS } from './crmStyles';
import { useCockpitDb } from '../cockpit/CockpitDataContext';
import {
  TEAM_STATUS, statusOf, comptes, compteOf, entitesOf, equipesOf,
  fichesOfTeam, initials, fullName,
} from './crmData';

const nodeKey = (...parts) => parts.join('¦');

// ─────────────────────────────────────────────────────────────────────────────
// Cartographie — l'organigramme d'un compte en lecture : Compte > Entités >
// Équipes > contacts rattachés. Vue de consultation ; la structure se modifie
// depuis le Périmètre.
// ─────────────────────────────────────────────────────────────────────────────

export default function CrmCartographie() {
  const { db } = useCockpitDb();
  const navigate = useNavigate();
  const { account: accountParam } = useParams();
  const urlAccount = accountParam ? decodeURIComponent(accountParam) : null;

  const list = comptes(db);
  const [sel, setSel] = useState(urlAccount || null);
  const account = sel || urlAccount || (list[0] && list[0].name) || null;
  const compte = compteOf(db, account);

  // On ne mémorise que les nœuds dont l'utilisateur a inversé l'état : les
  // entités restent dépliées par défaut, les équipes repliées, et changer de
  // compte n'oblige pas à réinitialiser le set.
  const [flipped, setFlipped] = useState(() => new Set());
  const isOpen = (key, byDefault) => (flipped.has(key) ? !byDefault : byDefault);
  const toggle = (key) => setFlipped((prev) => {
    const n = new Set(prev);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });

  const stats = useMemo(() => {
    const ents = entitesOf(compte);
    const eqs = ents.flatMap((e) => equipesOf(e));
    return { entites: ents.length, equipes: eqs.length };
  }, [compte]);

  if (!db) return null;

  // Sélecteur de compte, commun à toutes les vues CRM rattachées à un compte.
  const accbar = (
    <div className="crm-accbar">
      <span className="crm-accbar-lbl">Compte</span>
      <select
        className="crm-select"
        value={account || ''}
        onChange={(e) => { setSel(e.target.value); navigate(`/crm/cartographie/${encodeURIComponent(e.target.value)}`); }}
      >
        {list.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
      </select>
      <span className="sp" />
      <button className="btn btn-ghost" onClick={() => navigate(`/crm/perimetre/${encodeURIComponent(account)}`)}>
        Modifier le périmètre →
      </button>
    </div>
  );

  // Aucun compte : il n'y a pas d'organigramme à dessiner.
  if (!list.length) {
    return (
      <AppLayout title="CRM · Cartographie">
        <style>{COCKPIT_CSS}</style>
        <style>{CRM_CSS}</style>
        <div className="ckpt">
          <div className="ckpt-empty">
            <div className="ic">🏢</div>
            <div className="t">Aucun compte client</div>
            <div className="d">La cartographie dessine l'organigramme d'un compte : ses entités, ses équipes et les contacts qui y sont rattachés. Créez d'abord un compte client.</div>
            <button className="btn btn-primary" onClick={() => navigate('/crm/comptes')}>
              + Compte client
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Feuille : un contact référencé dans l'équipe.
  const ficheRow = (f) => (
    <div className="tn-row" key={`f${f.id}`} onClick={() => navigate(`/crm/fiches/${f.id}`)}>
      <span className="tn-tog">·</span>
      <span className="tn-ico p">{initials(fullName(f))}</span>
      <span className="tn-name" style={{ fontWeight: 600 }}>{fullName(f)}</span>
      <span className="tn-sub">{f.fonction || 'Fonction à compléter'}</span>
      {f.decisionnaire && <span className="crm-deci" title="Décisionnaire">★</span>}
    </div>
  );

  const teamNode = (entite, eq) => {
    const key = nodeKey(account, entite, eq.name);
    const fiches = fichesOfTeam(db, account, entite, eq.name);
    const open = isOpen(key, false);
    const st = TEAM_STATUS[statusOf(eq)];
    return (
      <div key={key}>
        <div className="tn-row" onClick={() => toggle(key)}>
          <span className="tn-tog">{fiches.length ? (open ? '▾' : '▸') : '·'}</span>
          <span className="tn-ico s">◇</span>
          <span className="tn-name">{eq.name}</span>
          <span className="tn-sub">
            <b style={{ color: st.color }}>{st.label}</b>
            {fiches.length ? ` · ${fiches.length} contact(s)` : ''}
          </span>
        </div>
        {open && fiches.length > 0 && <div className="tn-kids">{fiches.map(ficheRow)}</div>}
      </div>
    );
  };

  const entiteNode = (e) => {
    const key = nodeKey(account, e.name);
    const eqs = equipesOf(e);
    const open = isOpen(key, true);
    return (
      <div key={key}>
        <div className="tn-row" onClick={() => toggle(key)}>
          <span className="tn-tog">{eqs.length ? (open ? '▾' : '▸') : '·'}</span>
          <span className="tn-ico e">▢</span>
          <span className="tn-name">{e.name}</span>
          <span className="tn-sub">{eqs.length} équipe(s)</span>
        </div>
        {open && (
          <div className="tn-kids">
            {eqs.length
              ? eqs.map((eq) => teamNode(e.name, eq))
              : <div className="crm-team-empty" style={{ paddingLeft: 9 }}>Aucune équipe dans cette entité.</div>}
          </div>
        )}
      </div>
    );
  };

  return (
    <AppLayout title="CRM · Cartographie">
      <style>{COCKPIT_CSS}</style>
      <style>{CRM_CSS}</style>
      <div className="ckpt">

        {accbar}

        {!stats.entites ? (
          <div className="ckpt-empty">
            <div className="ic">🗺️</div>
            <div className="t">Compte non cartographié</div>
            <div className="d">
              « {account} » n'a encore aucune entité. Décrivez son périmètre (entités, puis équipes)
              pour voir apparaître son organigramme ici.
            </div>
            <button className="btn btn-primary" onClick={() => navigate(`/crm/perimetre/${encodeURIComponent(account)}`)}>
              Construire le périmètre
            </button>
          </div>
        ) : (
          <>
            <div className="acc-sec-head">
              <div className="t">Organigramme</div>
              <div className="c">{stats.entites} entité(s) · {stats.equipes} équipe(s) · cliquez un nœud pour déplier</div>
            </div>

            <div className="acc-tree">
              <div className="tn-row noh">
                <span className="tn-tog">·</span>
                <span className="tn-ico gc">{initials(account)}</span>
                <span className="tn-name">{account}</span>
                <span className="tn-sub">{stats.entites} entité(s) · {stats.equipes} équipe(s)</span>
              </div>
              <div className="tn-kids" style={{ borderLeft: 'none', paddingLeft: 4, marginLeft: 6 }}>
                {entitesOf(compte).map(entiteNode)}
              </div>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
